/**
 * Detects which environment the app is running in from the page's hostname,
 * and maps it to the API base URL. Detection happens at runtime because the
 * prod (main) and staging (develop) deploys run the identical build.
 *
 * Only the production domains are prod; every other host — the staging
 * bucket, localhost, previews — talks to the staging API.
 */
export type AppEnvironment = "prod" | "staging";

const PROD_HOSTNAMES = ["sysdesignplayground.com", "www.sysdesignplayground.com"];

const API_BASE_URLS: Record<AppEnvironment, string> = {
  prod: "https://api.sysdesignplayground.com",
  staging: "https://x7e85z0pz9.execute-api.us-east-1.amazonaws.com/Prod",
};

export function getAppEnvironment(hostname: string = window.location.hostname): AppEnvironment {
  return PROD_HOSTNAMES.includes(hostname.toLowerCase()) ? "prod" : "staging";
}

export function getApiBaseUrl(hostname?: string): string {
  return API_BASE_URLS[getAppEnvironment(hostname)];
}

// ---------------------------------------------------------------------------
// Sign-in (Amazon Cognito)
// ---------------------------------------------------------------------------

export interface AuthConfig {
  region: string;
  /** Cognito app client id (public; empty while the environment has no user pool). */
  clientId: string;
  /** Cognito hosted sign-in base URL, used for Google sign-in. */
  hostedUiDomain: string;
}

const AUTH_CONFIGS: Record<AppEnvironment, AuthConfig> = {
  prod: {
    region: "us-east-1",
    // Filled in once the prod sdp-api stack has deployed its user pool (UserPoolClientId output).
    clientId: "",
    hostedUiDomain: "https://sysdesignplayground.auth.us-east-1.amazoncognito.com",
  },
  staging: {
    region: "us-east-1",
    clientId: "7df8s28c536oktmhcai2402mul",
    hostedUiDomain: "https://sysdesignplayground-staging.auth.us-east-1.amazoncognito.com",
  },
};

export function getAuthConfig(hostname?: string): AuthConfig {
  return AUTH_CONFIGS[getAppEnvironment(hostname)];
}

/** Accounts are only required where the environment has a user pool to sign in to. */
export function isAuthEnabled(hostname?: string): boolean {
  return getAuthConfig(hostname).clientId !== "";
}

// Non-prod origins listed in the staging app client's OAuth callback URLs (API repo staging.yml)
const STAGING_OAUTH_ORIGINS = ["https://staging.sysdesignplayground.com", "http://localhost:3000"];

/**
 * Google sign-in redirects back to `/auth/callback` on this origin, which Cognito only
 * accepts for the production domains and the staging origins above.
 */
export function isGoogleSignInEnabled(location: Pick<Location, "hostname" | "origin"> = window.location): boolean {
  if (!isAuthEnabled(location.hostname)) return false;
  if (getAppEnvironment(location.hostname) === "prod") return true;
  return STAGING_OAUTH_ORIGINS.includes(location.origin);
}
